import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Gift,
  Users,
  DollarSign,
  Copy,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  User,
  Mail,
  Calendar,
  CreditCard,
  Plus,
  Link,
  Unlink,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  getUserReferralInfo,
  ReferredUser,
  ReferralReward,
  linkUserToReferrer,
  unlinkUserFromReferrer,
} from "@/api/referrals";

interface ReferralInfoProps {
  userId: number;
  userName: string;
  className?: string;
  hasAdminPrivileges?: boolean;
  canManageUser?: boolean;
}

interface ReferralData {
  referralCode?: string;
  referredBy?: { name: string; email: string };
  totalReferredUsers: number;
  totalPaidSubscribers: number;
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
  currentCycleProgress: number;
  nextRewardAmount: number;
  referredUsers: ReferredUser[];
  rewards: ReferralReward[];
}

const ReferralInfo = ({
  userId,
  userName,
  className,
  hasAdminPrivileges = false,
  canManageUser = false,
}: ReferralInfoProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [copiedCode, setCopiedCode] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState("");

  const {
    data: referralData,
    isLoading,
    error,
    refetch,
  } = useQuery<ReferralData>({
    queryKey: ["userReferralInfo", userId],
    queryFn: () => getUserReferralInfo(userId),
    enabled: !!userId,
  });

  // Mutation for linking referrer
  const linkReferrerMutation = useMutation({
    mutationFn: (referralCode: string) =>
      linkUserToReferrer(userId, referralCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userReferralInfo", userId] });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      toast({
        title: "Referrer linked successfully",
        description: `${userName} has been linked to their referrer.`,
      });
      setIsLinkDialogOpen(false);
      setReferralCodeInput("");
      refetch();
    },
    onError: (error) => {
      toast({
        title: "Error linking referrer",
        description:
          error instanceof Error
            ? error.message
            : "Failed to link referrer. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation for unlinking referrer
  const unlinkReferrerMutation = useMutation({
    mutationFn: () => unlinkUserFromReferrer(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userReferralInfo", userId] });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      toast({
        title: "Referrer unlinked successfully",
        description: `${userName} has been unlinked from their referrer.`,
      });
      setIsUnlinkDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      toast({
        title: "Error unlinking referrer",
        description:
          error instanceof Error
            ? error.message
            : "Failed to unlink referrer. Please try again.",
        variant: "destructive",
      });
    },
  });

  const copyReferralCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(true);
      toast({
        title: "Copied!",
        description: "Referral code copied to clipboard",
      });
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy referral code to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleLinkReferrer = () => {
    if (!referralCodeInput.trim()) {
      toast({
        title: "Invalid input",
        description: "Please enter a referral code.",
        variant: "destructive",
      });
      return;
    }
    linkReferrerMutation.mutate(referralCodeInput.trim().toUpperCase());
  };

  const handleUnlinkReferrer = () => {
    unlinkReferrerMutation.mutate();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge
            variant="outline"
            className="bg-yellow-50 text-yellow-700 border-yellow-200"
          >
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "paid":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Paid
          </Badge>
        );
      case "failed":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200"
          >
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Referral Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Referral Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Failed to load referral information
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!referralData) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Referral Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No referral data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasReferralActivity =
    referralData.referralCode ||
    referralData.referredBy ||
    referralData.totalReferredUsers > 0 ||
    referralData.rewards.length > 0;

  if (!hasReferralActivity) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Referral Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No referral activity for {userName}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5" />
          Referral Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="referred-users">Referred Users</TabsTrigger>
            <TabsTrigger value="rewards">Reward History</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Referral Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Referral Code */}
              {referralData.referralCode && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      Referral Code
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        copyReferralCode(referralData.referralCode!)
                      }
                      className="h-6 w-6 p-0"
                    >
                      {copiedCode ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <Badge variant="outline" className="font-mono text-base">
                    {referralData.referralCode}
                  </Badge>
                </div>
              )}

              {/* Referred By */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Referred By
                  </h4>
                  {hasAdminPrivileges && canManageUser && (
                    <div className="flex gap-1">
                      {!referralData.referredBy && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsLinkDialogOpen(true)}
                          className="h-6 w-6 p-0"
                          title="Link referrer"
                        >
                          <Link className="h-3 w-3 text-green-600" />
                        </Button>
                      )}
                      {referralData.referredBy && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsUnlinkDialogOpen(true)}
                          className="h-6 w-6 p-0"
                          title="Unlink referrer"
                        >
                          <Unlink className="h-3 w-3 text-red-600" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {referralData.referredBy ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div className="font-medium">
                        {referralData.referredBy.name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div className="text-sm text-muted-foreground">
                        {referralData.referredBy.email}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground italic">
                      No referrer assigned
                    </div>
                    {hasAdminPrivileges && canManageUser && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsLinkDialogOpen(true)}
                        className="w-full"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Link Referrer
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 text-center">
                <Users className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold">
                  {referralData.totalReferredUsers}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total Referred
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 text-center">
                <CreditCard className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold">
                  {referralData.totalPaidSubscribers}
                </div>
                <div className="text-xs text-muted-foreground">
                  Paid Subscribers
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4 text-center">
                <DollarSign className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold">
                  ₹{referralData.totalEarnings.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total Earnings
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 text-center">
                <Clock className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                <div className="text-2xl font-bold">
                  ₹{referralData.pendingEarnings.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
            </div>

            {/* Gamified Referral Progress */}
            {referralData.totalPaidSubscribers >= 0 && (
              <div className="space-y-4">
                {/* Current Progress */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 rounded-lg p-4 border border-blue-200/50 dark:border-blue-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Gift className="h-4 w-4 text-blue-600" />
                      Current Progress
                    </h4>
                    <span className="text-sm text-muted-foreground bg-white/60 dark:bg-black/20 px-2 py-1 rounded-full">
                      {referralData.currentCycleProgress}/5 referrals
                    </span>
                  </div>
                  <div className="w-full bg-muted/50 rounded-full h-3 mb-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out relative"
                      style={{
                        width: `${
                          (referralData.currentCycleProgress / 5) * 100
                        }%`,
                      }}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Next reward: ₹{referralData.nextRewardAmount.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {5 - referralData.currentCycleProgress} more needed
                    </div>
                  </div>
                  {referralData.currentCycleProgress === 0 && (
                    <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 px-3 py-2 rounded-md">
                      🎯 Get your first 5 paid subscribers to unlock your first
                      reward!
                    </div>
                  )}
                </div>

                {/* Referral Roadmap */}
                <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 dark:from-amber-950/10 dark:via-orange-950/10 dark:to-red-950/10 rounded-lg p-4 border border-amber-200/50 dark:border-amber-800/30">
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-4">
                    <svg
                      className="h-4 w-4 text-amber-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Referral Rewards Roadmap
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      {
                        cycle: 1,
                        referrals: 5,
                        reward: 200,
                        totalReward: 200,
                        color: "emerald",
                        completed: referralData.totalPaidSubscribers >= 5,
                      },
                      {
                        cycle: 2,
                        referrals: 10,
                        reward: 220,
                        totalReward: 420, // 200 + 220
                        color: "blue",
                        completed: referralData.totalPaidSubscribers >= 10,
                      },
                      {
                        cycle: 3,
                        referrals: 15,
                        reward: 242,
                        totalReward: 662, // 200 + 220 + 242
                        color: "purple",
                        completed: referralData.totalPaidSubscribers >= 15,
                      },
                      {
                        cycle: 4,
                        referrals: 20,
                        reward: 266.2,
                        totalReward: 928.2, // 200 + 220 + 242 + 266.2
                        color: "pink",
                        completed: referralData.totalPaidSubscribers >= 20,
                      },
                    ].map((milestone, index) => {
                      const isActive =
                        Math.floor(referralData.totalPaidSubscribers / 5) +
                          1 ===
                        milestone.cycle;
                      const colorClasses = {
                        emerald: milestone.completed
                          ? "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200"
                          : isActive
                          ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-300 dark:ring-emerald-700"
                          : "bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400",
                        blue: milestone.completed
                          ? "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200"
                          : isActive
                          ? "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 ring-2 ring-blue-300 dark:ring-blue-700"
                          : "bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400",
                        purple: milestone.completed
                          ? "bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-800 dark:text-purple-200"
                          : isActive
                          ? "bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 ring-2 ring-purple-300 dark:ring-purple-700"
                          : "bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400",
                        pink: milestone.completed
                          ? "bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700 text-pink-800 dark:text-pink-200"
                          : isActive
                          ? "bg-pink-50 dark:bg-pink-950/20 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300 ring-2 ring-pink-300 dark:ring-pink-700"
                          : "bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400",
                      };

                      return (
                        <div
                          key={milestone.cycle}
                          className={cn(
                            "relative p-3 rounded-lg border-2 transition-all duration-300",
                            colorClasses[
                              milestone.color as keyof typeof colorClasses
                            ],
                            isActive && "transform scale-105 shadow-lg"
                          )}
                        >
                          {milestone.completed && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 text-white" />
                            </div>
                          )}
                          {isActive && !milestone.completed && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                              <Clock className="h-4 w-4 text-white" />
                            </div>
                          )}
                          <div className="text-center">
                            <div className="text-xs font-medium opacity-75 mb-1">
                              Cycle {milestone.cycle}
                            </div>
                            <div className="space-y-1">
                              <div className="text-sm font-bold text-green-600 dark:text-green-400">
                                +₹{milestone.reward.toFixed(0)}
                              </div>
                              <div className="text-lg font-bold">
                                ₹{milestone.totalReward.toFixed(0)}
                              </div>
                              <div className="text-xs opacity-60">
                                total earned
                              </div>
                            </div>
                            <div className="text-xs opacity-75 mt-1">
                              {milestone.referrals} referrals
                            </div>
                            {isActive && !milestone.completed && (
                              <div className="text-xs mt-1 font-medium">
                                {milestone.referrals -
                                  referralData.totalPaidSubscribers}{" "}
                                more needed
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 text-xs text-center text-muted-foreground bg-white/40 dark:bg-black/20 px-3 py-2 rounded-md">
                    💡 Each cycle increases by 10%! Keep referring to earn more
                    rewards.
                  </div>
                </div>

                {/* Achievement Badges */}
                {referralData.totalPaidSubscribers > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-950/20 dark:to-teal-950/20 rounded-lg p-4 border border-green-200/50 dark:border-green-800/50">
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                      <svg
                        className="h-4 w-4 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Your Achievements
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {referralData.totalPaidSubscribers >= 1 && (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200">
                          🎯 First Referral
                        </Badge>
                      )}
                      {referralData.totalPaidSubscribers >= 5 && (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200">
                          🏆 First Milestone
                        </Badge>
                      )}
                      {referralData.totalPaidSubscribers >= 10 && (
                        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200">
                          🚀 Super Referrer
                        </Badge>
                      )}
                      {referralData.totalPaidSubscribers >= 15 && (
                        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-200">
                          👑 Referral Master
                        </Badge>
                      )}
                      {referralData.totalPaidSubscribers >= 20 && (
                        <Badge className="bg-pink-100 text-pink-800 hover:bg-pink-200 dark:bg-pink-900 dark:text-pink-200">
                          💎 Referral Legend
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="referred-users" className="space-y-4">
            {referralData.referredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No referred users yet
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Registered Date</TableHead>
                      <TableHead>Subscribed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referralData.referredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {user.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {format(new Date(user.created_at), "MMM dd, yyyy")}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.has_subscription ? (
                            <Badge className="bg-green-500 hover:bg-green-600">
                              Yes
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-muted-foreground"
                            >
                              No
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="rewards" className="space-y-4">
            {referralData.rewards.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No reward history yet
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cycle</TableHead>
                      <TableHead>Reward Amount</TableHead>
                      <TableHead>Subscribers</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referralData.rewards.map((reward) => (
                      <TableRow key={reward.id}>
                        <TableCell className="font-medium">
                          <Badge variant="outline">
                            Cycle #{reward.cycle_number}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">
                          ₹{reward.reward_amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {reward.subscribers_in_cycle} subscribers
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(reward.reward_status)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {format(
                              new Date(reward.created_at),
                              "MMM dd, yyyy"
                            )}
                          </div>
                          {reward.paid_at && (
                            <div className="text-xs text-muted-foreground">
                              Paid:{" "}
                              {format(new Date(reward.paid_at), "MMM dd, yyyy")}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Link Referrer Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-green-600" />
              Link Referrer for {userName}
            </DialogTitle>
            <DialogDescription>
              Enter the referral code of the user who referred {userName}. This
              action will establish the referral relationship.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="referralCode" className="text-sm font-medium">
                Referral Code
              </label>
              <Input
                id="referralCode"
                placeholder="Enter 5-character referral code"
                value={referralCodeInput}
                onChange={(e) =>
                  setReferralCodeInput(e.target.value.toUpperCase())
                }
                maxLength={5}
                className="mt-1"
                disabled={linkReferrerMutation.isPending}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Referral codes are 5-character alphanumeric codes (e.g., ABC12)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsLinkDialogOpen(false);
                setReferralCodeInput("");
              }}
              disabled={linkReferrerMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkReferrer}
              disabled={
                linkReferrerMutation.isPending || !referralCodeInput.trim()
              }
            >
              {linkReferrerMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Linking...
                </>
              ) : (
                <>
                  <Link className="mr-2 h-4 w-4" />
                  Link Referrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Referrer Dialog */}
      <Dialog open={isUnlinkDialogOpen} onOpenChange={setIsUnlinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unlink Referrer for {userName}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the referral relationship between{" "}
              {userName} and {referralData?.referredBy?.name}? This action
              cannot be undone and may affect referral rewards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsUnlinkDialogOpen(false)}
              disabled={unlinkReferrerMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlinkReferrer}
              disabled={unlinkReferrerMutation.isPending}
            >
              {unlinkReferrerMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unlinking...
                </>
              ) : (
                <>
                  <Unlink className="mr-2 h-4 w-4" />
                  Unlink Referrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ReferralInfo;
